import React, { useState, useEffect } from "react";
import { usePagination } from "../hooks";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Button,
  InputBase,
  Divider,
  IconButton,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { translateText } from "../translations/translationService";
import { useSelector } from "react-redux";
import {
  Search as SearchIcon,
  Pending as PendingIcon,
  History as HistoryIcon,
  Build as BuildIcon,
  GetApp as GetAppIcon,
  FilterList as FilterListIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  getFullReparacionesInternas,
  getFullReparacionesExternas,
  getSavedReparaciones,
  getFullWorkRepair,
  insertDatosReparaciones,
} from "../services/ConnectDataBase";
import { SCREEN_IDS } from "../components/FullComponent.js";
import { getErrorMessage, notify } from "../services/ui/notify";
import * as XLSX from "xlsx";
import { BRAND_COLORS, BORDER_COLORS, TEXT_COLORS, BACKGROUND_COLORS, CONTROL_REPARACIONES_SCREEN_COLORS } from "../style/colors.js";

export default function ControlDeReparacionesScreen() {
  const lang = useSelector((state) => state.lang.value);
  const name = useSelector((state) => state.user.name);
  const [repairData, setRepairData] = useState([]);
  const [savedReparaciones, setSavedReparaciones] = useState([]);
  const [workOptions, setWorkOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [appliedSearchTerms, setAppliedSearchTerms] = useState([]);
  const [repInternas, setRepInternas] = useState([]);
  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 100);
  const [currentView, setCurrentView] = useState("Pendientes");

  const [historicoDate, setHistoricoDate] = useState("");
  const [internoDate, setInternoDate] = useState("");

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const fetchWork = async () => {
      try {
        const work = await getFullWorkRepair(
          SCREEN_IDS.GENERAL,
          "TrabajosReparaciones"
        );

        setWorkOptions(work || []);
      } catch (error) {
        console.error("Error al cargar trabajos:", error);
      }
    };

    fetchWork();
  }, []);

  useEffect(() => {
    const fetchWork = async () => {
      try {
        const work = await getFullReparacionesInternas(
          SCREEN_IDS.GENERAL,
          "reparacionesInternas"
        );

        setRepInternas(work || []);
      } catch (error) {
        console.error("Error al cargar trabajos:", error);
      }
    };

    fetchWork();
  }, []);

  useEffect(() => {
    const fetchSavedRepair = async () => {
      try {
        const saved = await getSavedReparaciones(
          SCREEN_IDS.GENERAL,
          "DatosGuardadosReparaciones"
        );
        setSavedReparaciones(saved || []);
      } catch (error) {
        console.error("Error fetching saved repairs:", error);
        setSavedReparaciones([]);
      }
    };

    fetchSavedRepair();
  }, []);

  const handleClearSearch = () => {
    setSearchText("");
    setAppliedSearchTerms([]);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await getFullReparacionesExternas(
          SCREEN_IDS.GENERAL,
          "reparacionesExternas"
        );

        const flattenedData = Object.values(data).flat();
        const uniqueData = flattenedData.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.mo === item.mo)
        );

        const formattedData = uniqueData.map((item) => {
          const baseData = {
            numero: item.mo,
            fechaEntrega: item.fecha_entrega_pieza?.date?.split(" ")[0] || "",
            trabajo:
              workOptions.find((w) => w.descripcion === item.tipo)
                ?.descripcion || "",
            referencia: item.referencia || "",
            observaciones: item.observaciones || "Sin observaciones",
            pesoEntregado: item.peso_entregado
              ? parseFloat(item.peso_entregado).toFixed(2)
              : "",
            pesoDevolucion: item.peso_devolucion
              ? parseFloat(item.peso_devolucion).toFixed(2)
              : "",
            diferencia: "0.00",
            merma: "0.000",
            fechaDevolucion: "",
            precio: "",
          };

          const savedItem = savedReparaciones.find(
            (saved) => saved.mo === item.mo
          );

          if (savedItem) {
            baseData.pesoEntregado =
              parseFloat(savedItem.PesoEntregado) > 0
                ? parseFloat(savedItem.PesoEntregado).toFixed(2)
                : baseData.pesoEntregado;

            baseData.pesoDevolucion =
              parseFloat(savedItem.PesoDevolucion) > 0
                ? parseFloat(savedItem.PesoDevolucion).toFixed(2)
                : baseData.pesoDevolucion;

            baseData.trabajo = savedItem.trabajo || baseData.trabajo;

            baseData.precio =
              parseFloat(savedItem.precio) > 0
                ? parseFloat(savedItem.precio).toFixed(2)
                : baseData.precio;

            baseData.fechaDevolucion =
              savedItem.FechaDevolucion?.date.split(" ")[0] ||
              baseData.fechaDevolucion;
          }

          if (
            parseFloat(baseData.pesoEntregado) > 0 &&
            parseFloat(baseData.pesoDevolucion) > 0
          ) {
            baseData.diferencia = Math.abs(
              parseFloat(baseData.pesoEntregado) -
                parseFloat(baseData.pesoDevolucion)
            ).toFixed(2);

            baseData.merma = (
              Math.abs(
                parseFloat(baseData.pesoEntregado) -
                  parseFloat(baseData.pesoDevolucion)
              ) * 0.03
            ).toFixed(3);
          }

          return baseData;
        });

        setRepairData(formattedData);
      } catch (error) {
        console.error("Error al cargar los datos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (savedReparaciones.length > 0 && workOptions.length > 0) {
      fetchData();
    }
  }, [savedReparaciones, workOptions]);

  const handleInputChange = (index, field, value) => {
    const originalIndex = repairData.findIndex(
      (item) => item.numero === filteredRepairData[index].numero
    );

    if (originalIndex !== -1) {
      const updatedData = [...repairData];
      const updatedRow = { ...updatedData[originalIndex], [field]: value };

      if (field === "pesoEntregado" || field === "pesoDevolucion") {
        const pesoEntregado = parseFloat(updatedRow.pesoEntregado);
        const pesoDevolucion = parseFloat(updatedRow.pesoDevolucion);

        if (
          !isNaN(pesoEntregado) &&
          pesoEntregado > 0 &&
          !isNaN(pesoDevolucion) &&
          pesoDevolucion > 0
        ) {
          updatedRow.diferencia = Math.abs(
            pesoEntregado - pesoDevolucion
          ).toFixed(2);
          updatedRow.merma = (
            Math.abs(pesoEntregado - pesoDevolucion) * 0.03
          ).toFixed(3);
        } else {
          updatedRow.diferencia = "0.00";
          updatedRow.merma = "0.000";
        }
      }

      if (field === "precio") {
        updatedRow.precio = parseFloat(value) || 0;
      }

      updatedData[originalIndex] = updatedRow;
      setRepairData(updatedData);
    }
  };

  const formatSearchInput = (input) => {
    const cleanInput = input.replace(/ /g, "");
    const formattedInput = cleanInput
      .split("")
      .map((char, index) =>
        index > 0 && (index + 1) % 10 === 0 ? char + " " : char
      )
      .join("");

    return formattedInput.trim();
  };

  const handleSearchTextChange = (e) => {
    const formattedText = formatSearchInput(e.target.value);
    setSearchText(formattedText);
  };


  const handleSaveData = async () => {
    try {
      const datosParaGuardar = repairData
        .filter((item) => {
          return (
            parseFloat(item.pesoEntregado) > 0 ||
            parseFloat(item.pesoDevolucion) > 0 ||
            parseFloat(item.precio) > 0 ||
            item.observaciones
          );
        })
        .map((item) => {
          return {
            ...item,
            usuario: name,
          };
        });

      if (datosParaGuardar.length === 0) {
        notify.info("No hay datos válidos para guardar.");
        return;
      }

      await insertDatosReparaciones(
        SCREEN_IDS.GENERAL,
        "insertarDatosReparaciones",
        datosParaGuardar
      );

      notify.success("Datos guardados con éxito");
      console.log("Datos guardados:", datosParaGuardar);
    } catch (error) {
      console.error("Error guardando datos:", error);
      notify.error(getErrorMessage(error, "Error al guardar datos"));
    }
  };

  const filteredHistoricoByDate = (savedReparaciones || []).filter((item) => {
    const itemDate = item.fecha?.date?.split(" ")[0] || "";
    return historicoDate === "" || itemDate === historicoDate;
  });

  const handleExportToExcel = () => {
    const dataToExport = filteredHistoricoByDate.map((item) => ({
      "Nº Reparación": item.mo,
      "Fecha Entrega": item.fecha?.date?.split(" ")[0] || "",
      Trabajo: item.trabajo || "",
      Referencia: item.referencia || "",
      Observaciones: item.observaciones || "Sin observaciones",
      "Peso Entregado": parseFloat(item.PesoEntregado || 0),
      "Peso Devolución": parseFloat(item.PesoDevolucion || 0),
      Diferencia: parseFloat(
        Math.abs(
          (parseFloat(item.PesoEntregado) || 0) -
            (parseFloat(item.PesoDevolucion) || 0)
        )
      ),
      Merma: parseFloat(
        (
          Math.abs(
            (parseFloat(item.PesoEntregado) || 0) -
              (parseFloat(item.PesoDevolucion) || 0)
          ) * 0.03
        ).toFixed(4)
      ),
      "Fecha Devolución": item.FechaDevolucion?.date?.split(" ")[0] || "",
      Precio: parseFloat(item.precio || 0),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let R = 1; R <= range.e.r; ++R) {
      const fechaEntrega = worksheet[`B${R + 1}`]?.v;
      if (fechaEntrega) {
        worksheet[`B${R + 1}`].t = "d";
      }

      const fechaDevolucion = worksheet[`J${R + 1}`]?.v;
      if (fechaDevolucion) {
        worksheet[`J${R + 1}`].t = "d";
      }

      const pesoEntregado = worksheet[`F${R + 1}`]?.v;
      const pesoDevolucion = worksheet[`G${R + 1}`]?.v;
      const diferencia = worksheet[`H${R + 1}`]?.v;
      const merma = worksheet[`I${R + 1}`]?.v;
      const precio = worksheet[`K${R + 1}`]?.v;

      if (pesoEntregado !== undefined) worksheet[`F${R + 1}`].t = "n";
      if (pesoDevolucion !== undefined) worksheet[`G${R + 1}`].t = "n";
      if (diferencia !== undefined) worksheet[`H${R + 1}`].t = "n";
      if (merma !== undefined) worksheet[`I${R + 1}`].t = "n";
      if (precio !== undefined) worksheet[`K${R + 1}`].t = "n";
    }

    worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Histórico");

    XLSX.writeFile(workbook, "Historico_Reparaciones.xlsx");
  };

  const handleExportInternosToExcel = () => {
    const dataToExport = repInternas
      .filter((item) => {
        const itemDate = item.fecha_ini?.date?.split(" ")[0] || "";
        return internoDate === "" || itemDate === internoDate;
      })
      .map((item) => ({
        "Nº Reparación": item.mo,
        Fecha: formatFecha(item.fecha_ini?.date),
        Referencia: item.referencia || "",
        Cantidad: parseFloat(item.cantidad || 0),
        Observaciones: item.observaciones || "Sin observaciones",
      }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let row = 1; row <= range.e.r; ++row) {
      const fechaCellRef = `B${row + 1}`;
      const cantidadCellRef = `D${row + 1}`;

      const fechaStr = worksheet[fechaCellRef]?.v;
      if (fechaStr) {
        const [dd, mm, yyyy] = fechaStr.split("/");
        const dateObj = new Date(`${yyyy}-${mm}-${dd}`);
        worksheet[fechaCellRef].v = dateObj;
        worksheet[fechaCellRef].t = "d";
        worksheet[fechaCellRef].z = "dd/mm/yyyy";
      }

      if (worksheet[cantidadCellRef]) {
        worksheet[cantidadCellRef].t = "n";
      }
    }

    const columnWidths = Object.keys(dataToExport[0]).map((key) => {
      const maxLength = Math.max(
        key.length,
        ...dataToExport.map((row) =>
          row[key] ? row[key].toString().length : 0
        )
      );
      return { wch: maxLength + 2 };
    });
    worksheet["!cols"] = columnWidths;

    worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reparaciones Internas");
    XLSX.writeFile(workbook, "Reparaciones_Internas.xlsx");
  };

  const totalPrecio = repairData.reduce(
    (sum, item) => sum + (parseFloat(item.precio) || 0),
    0
  );

  const totalMerma = repairData.reduce(
    (sum, item) => sum + (parseFloat(item.merma) || 0),
    0
  );

  const lowerSearchText = searchText.toLowerCase();

  const filteredRepairData = repairData.filter((item) => {
    if (appliedSearchTerms.length === 0) return true;

    return appliedSearchTerms.some((term) =>
      item.numero.toLowerCase().includes(term.toLowerCase())
    );
  });

  const handleSearchClick = () => {
    const searchTerms = searchText.trim().split(" ").filter(Boolean);

    if (searchTerms.length === 0) {
      notify.info("Introduce al menos un término de búsqueda.");
      return;
    }

    setAppliedSearchTerms(searchTerms);
  };

  const filteredHistoricoData = savedReparaciones.filter((item) => {
    const itemDate = item.fecha?.date?.split(" ")[0] || "";
    const numero = item.mo?.toLowerCase() || "";
    const referencia = item.Referencia?.toLowerCase() || "";
    const observaciones = item.Observaciones?.toLowerCase() || "";

    const matchesDate = historicoDate === "" || itemDate === historicoDate;
    const matchesSearch =
      numero.includes(lowerSearchText) ||
      referencia.includes(lowerSearchText) ||
      observaciones.includes(lowerSearchText);

    return matchesDate && matchesSearch;
  });

  const filteredSavedReparaciones = savedReparaciones.filter((item) => {
    const mo = item.mo?.toLowerCase() || "";
    const referencia = item.Referencia?.toLowerCase() || "";
    const observaciones = item.Observaciones?.toLowerCase() || "";
    return (
      mo.includes(lowerSearchText) ||
      referencia.includes(lowerSearchText) ||
      observaciones.includes(lowerSearchText)
    );
  });

  // Skeleton loader para mejor UX
  const SkeletonRowPendientes = () => (
    <TableRow>
      {[...Array(11)].map((_, i) => (
        <TableCell key={i} sx={{ py: 0.75, px: 1.5 }}>
          <Skeleton variant="text" width="100%" height={20} />
        </TableCell>
      ))}
    </TableRow>
  );

  const SkeletonRowInternos = () => (
    <TableRow>
      {[...Array(5)].map((_, i) => (
        <TableCell key={i} sx={{ py: 0.75, px: 1.5 }}>
          <Skeleton variant="text" width="100%" height={20} />
        </TableCell>
      ))}
    </TableRow>
  );

  const renderPendientesTable = () => {
    return (
      <>
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT}`,
          }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        translateText("Nº Reparación", lang),
                        translateText("Fecha Entrega", lang),
                        translateText("Trabajo", lang),
                        translateText("Referencia", lang),
                        translateText("Observaciones", lang),
                        translateText("Peso Entregado", lang),
                        translateText("Peso Devolución", lang),
                        translateText("Diferencia", lang),
                        translateText("Merma", lang),
                        translateText("Fecha Devolución", lang),
                        translateText("Precio (€)", lang),
                      ].map((header, i) => (
                        <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={24} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(5)].map((_, i) => (
                      <SkeletonRowPendientes key={i} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(78vh - 20px)" }}>
              <Table stickyHeader size="small">
                <TableHead>
                <TableRow>
                  {[
                    translateText("Nº Reparación", lang),
                    translateText("Fecha Entrega", lang),
                    translateText("Trabajo", lang),
                    translateText("Referencia", lang),
                    translateText("Observaciones", lang),
                    translateText("Peso Entregado", lang),
                    translateText("Peso Devolución", lang),
                    translateText("Diferencia", lang),
                    translateText("Merma", lang),
                    translateText("Fecha Devolución", lang),
                    translateText("Precio (€)", lang),
                  ].map((header) => (
                    <TableCell
                      key={header}
                      align='center'
                      sx={{
                        fontWeight: 600,
                        backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                        borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                        fontSize: "0.75rem",
                        color: "black",
                        py: 1,
                        px: 1.5,
                        whiteSpace: "nowrap",
                      }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRepairData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": {
                          backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                        },
                        borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                      }}>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: TEXT_COLORS.PRIMARY,
                        }}>
                        {item.numero}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: TEXT_COLORS.PRIMARY,
                        }}>
                        {formatFecha(item.fechaEntrega)}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          py: 0.75,
                          px: 1.5,
                        }}>
                        <Select
                          size='small'
                          value={item.trabajo || ""}
                          onChange={(e) =>
                            handleInputChange(index, "trabajo", e.target.value)
                          }
                          sx={{
                            fontSize: "0.75rem",
                            minWidth: "100px",
                            backgroundColor: "white",
                            borderRadius: 1,
                            height: "28px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: BORDER_COLORS.DEFAULT,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: BRAND_COLORS.PRIMARY,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: BRAND_COLORS.PRIMARY,
                              borderWidth: "1px",
                            },
                            "& .MuiSelect-select": {
                              py: 0.5,
                              fontSize: "0.75rem",
                            },
                          }}>
                          {workOptions.map((option, idx) => (
                            <MenuItem key={idx} value={option.descripcion} sx={{ fontSize: "0.75rem" }}>
                              {option.descripcion}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: TEXT_COLORS.PRIMARY,
                        }}>
                        {item.referencia}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: TEXT_COLORS.PRIMARY,
                          maxWidth: 200,
                        }}>
                        <Tooltip title={item.observaciones || "Sin observaciones"} arrow>
                          <Typography 
                            sx={{ 
                              fontSize: "0.75rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "help",
                            }}>
                            {item.observaciones || "Sin observaciones"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <TextField
                          value={item.pesoEntregado}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "pesoEntregado",
                              e.target.value
                            )
                          }
                          type='number'
                          variant='outlined'
                          size='small'
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              height: "28px",
                              "& fieldset": {
                                borderColor: BORDER_COLORS.DEFAULT,
                              },
                              "&:hover fieldset": {
                                borderColor: BRAND_COLORS.PRIMARY,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: BRAND_COLORS.PRIMARY,
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              py: 0.5,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <TextField
                          value={item.pesoDevolucion}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "pesoDevolucion",
                              e.target.value
                            )
                          }
                          type='number'
                          variant='outlined'
                          size='small'
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              height: "28px",
                              "& fieldset": {
                                borderColor: BORDER_COLORS.DEFAULT,
                              },
                              "&:hover fieldset": {
                                borderColor: BRAND_COLORS.PRIMARY,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: BRAND_COLORS.PRIMARY,
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              py: 0.5,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: TEXT_COLORS.PRIMARY,
                        }}>
                        {item.diferencia}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: TEXT_COLORS.PRIMARY,
                        }}>
                        {item.merma}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: TEXT_COLORS.PRIMARY,
                        }}>
                        {formatFecha(item.fechaDevolucion)}
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <TextField
                          value={parseFloat(item.precio) || ""}
                          onChange={(e) =>
                            handleInputChange(index, "precio", e.target.value)
                          }
                          type='number'
                          variant='outlined'
                          size='small'
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              height: "28px",
                              "& fieldset": {
                                borderColor: BORDER_COLORS.DEFAULT,
                              },
                              "&:hover fieldset": {
                                borderColor: BRAND_COLORS.PRIMARY,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: BRAND_COLORS.PRIMARY,
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              py: 0.5,
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          {!isLoading && (
            <>
              <Divider />

              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                count={filteredRepairData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE,
                  "& .MuiTablePagination-toolbar": {
                    fontSize: "0.75rem",
                    color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </>
          )}
        </Paper>

        <Box
          sx={{
            marginTop: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <Typography variant='subtitle1' sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
            {translateText("Importe total", lang)}: {totalPrecio.toFixed(2)} € |{" "}
            {translateText("Merma total", lang)}: {totalMerma.toFixed(3)} g
          </Typography>
          <Button
            variant='outlined'
            size="small"
            sx={{
              backgroundColor:
                currentView === "Pendientes" ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY : "transparent",
              color: currentView === "Pendientes" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
              borderRadius: 2,
              fontSize: "0.8rem",
              fontWeight: 600,
              padding: "6px 16px",
              textTransform: "none",
              transition: "all 0.2s ease",
              boxShadow:
                currentView === "Pendientes"
                  ? "0 2px 8px rgba(70, 93, 100, 0.2)"
                  : "none",
              "&:hover": {
                backgroundColor:
                  currentView === "Pendientes"
                    ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY
                    : "rgba(70, 93, 100, 0.1)",
                color: currentView === "Pendientes" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
              },
            }}
            onClick={handleSaveData}>
            {translateText("Guardar Datos", lang)}
          </Button>
        </Box>
      </>
    );
  };
  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "";
    const [yyyy, mm, dd] = fechaStr.split(" ")[0].split("-");
    return `${dd}/${mm}/${yyyy}`;
  };

  const renderHistoricoTable = () => {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
            gap: 2,
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}>
            <TextField
              label={translateText("Seleccionar Fecha", lang)}
              type='date'
              value={historicoDate}
              onChange={(e) => setHistoricoDate(e.target.value)}
              size='small'
              InputLabelProps={{ shrink: true }}
              sx={{
                width: "140px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "white",
                  height: "32px",
                  "& fieldset": {
                    borderColor: BORDER_COLORS.DEFAULT,
                  },
                  "&:hover fieldset": {
                    borderColor: BRAND_COLORS.PRIMARY,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: BRAND_COLORS.PRIMARY,
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
                "& .MuiOutlinedInput-input": {
                  fontSize: "0.75rem",
                  py: 0.75,
                },
              }}
            />

            <Button
              variant='outlined'
              size='small'
              sx={{
                backgroundColor: "transparent",
                color: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PURPLE,
                border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_PURPLE}`,
                borderRadius: 2,
                fontSize: "0.75rem",
                fontWeight: 500,
                padding: "4px 12px",
                textTransform: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PURPLE,
                  color: "white",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 8px rgba(156, 39, 176, 0.2)",
                  border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_PURPLE}`,
                },
              }}
              onClick={() => setHistoricoDate("")}>
              {translateText("Quitar filtro", lang)}
            </Button>
          </Box>

          <Button
            variant='outlined'
            size='small'
            sx={{
              backgroundColor: "transparent",
              color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
              borderRadius: 2,
              fontSize: "0.75rem",
              fontWeight: 500,
              padding: "4px 12px",
              textTransform: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
                color: "white",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(70, 93, 100, 0.2)",
                border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
              },
            }}
            onClick={handleExportToExcel}>
            {translateText("Exportar excel", lang)}
          </Button>
        </Box>

        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT}`,
          }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        translateText("Nº Reparación", lang),
                        translateText("Fecha Entrega", lang),
                        translateText("Trabajo", lang),
                        translateText("Referencia", lang),
                        translateText("Observaciones", lang),
                        translateText("Peso Entregado", lang),
                        translateText("Peso Devolución", lang),
                        translateText("Diferencia", lang),
                        translateText("Merma", lang),
                        translateText("Fecha Devolución", lang),
                        translateText("Precio (€)", lang),
                      ].map((header, i) => (
                        <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={24} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(5)].map((_, i) => (
                      <SkeletonRowPendientes key={i} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(78vh - 20px)" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      translateText("Nº Reparación", lang),
                      translateText("Fecha Entrega", lang),
                      translateText("Trabajo", lang),
                      translateText("Referencia", lang),
                      translateText("Observaciones", lang),
                      translateText("Peso Entregado", lang),
                      translateText("Peso Devolución", lang),
                      translateText("Diferencia", lang),
                      translateText("Merma", lang),
                      translateText("Fecha Devolución", lang),
                      translateText("Precio (€)", lang),
                    ].map((header) => (
                      <TableCell
                        key={header}
                        align='center'
                        sx={{
                          fontWeight: 600,
                          backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_MEDIUM}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                          whiteSpace: "nowrap",
                        }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistoricoData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align='center' sx={{ py: 8 }}>
                      <Typography variant='h6' sx={{ color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
                        {translateText(
                          "No hay resultados para la búsqueda o la fecha seleccionada.",
                          lang
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHistoricoData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => {
                      const pesoEntregado = parseFloat(item.PesoEntregado) || 0;
                      const pesoDevolucion =
                        parseFloat(item.PesoDevolucion) || 0;
                      const diferencia =
                        pesoEntregado > 0 && pesoDevolucion > 0
                          ? Math.abs(pesoEntregado - pesoDevolucion).toFixed(2)
                          : "0.00";
                      const merma =
                        pesoEntregado > 0 && pesoDevolucion > 0
                          ? (
                              Math.abs(pesoEntregado - pesoDevolucion) * 0.03
                            ).toFixed(3)
                          : "0.000";

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": {
                              backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW,
                            },
                            borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_SECONDARY}`,
                          }}>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {item.mo}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {formatFecha(item.fecha?.date)}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {item.trabajo || ""}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {item.referencia || ""}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                              maxWidth: 200,
                            }}>
                            <Tooltip title={item.Observaciones || "Sin observaciones"} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  cursor: "help",
                                }}>
                                {item.Observaciones || "Sin observaciones"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {pesoEntregado > 0 ? pesoEntregado.toFixed(2) : ""}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {pesoDevolucion > 0
                              ? pesoDevolucion.toFixed(2)
                              : ""}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {diferencia}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {merma}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {item.FechaDevolucion?.date.split(" ")[0] || ""}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.75,
                              px: 1.5,
                              color: TEXT_COLORS.PRIMARY,
                            }}>
                            {item.precio
                              ? parseFloat(item.precio).toFixed(2)
                              : ""}
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          {!isLoading && (
            <>
              <Divider />
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                count={filteredHistoricoData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE,
                  "& .MuiTablePagination-toolbar": {
                    fontSize: "0.75rem",
                    color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </>
          )}
        </Paper>
      </>
    );
  };

  const renderInternoTable = (data) => {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
            gap: 2,
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}></Box>
          <Button
            variant='outlined'
            size='small'
            sx={{
              backgroundColor: "transparent",
              color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
              borderRadius: 2,
              fontSize: "0.75rem",
              fontWeight: 500,
              padding: "4px 12px",
              textTransform: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
                color: "white",
                transform: "translateY(-1px)",
                boxShadow: `0 2px 8px ${CONTROL_REPARACIONES_SCREEN_COLORS.SHADOW_DARK}`,
                border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
              },
            }}
            onClick={handleExportInternosToExcel}>
            {translateText("Exportar excel", lang)}
          </Button>
        </Box>

        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        translateText("Nº Reparación", lang),
                        translateText("Fecha", lang),
                        translateText("Referencia", lang),
                        translateText("Cantidad", lang),
                        translateText("Observaciones", lang),
                      ].map((header, i) => (
                        <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={24} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(5)].map((_, i) => (
                      <SkeletonRowInternos key={i} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(78vh - 20px)" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      translateText("Nº Reparación", lang),
                      translateText("Fecha", lang),
                      translateText("Referencia", lang),
                      translateText("Cantidad", lang),
                      translateText("Observaciones", lang),
                    ].map((header) => (
                      <TableCell
                        key={header}
                        align='center'
                        sx={{
                          fontWeight: 600,
                          backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_MEDIUM}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                          whiteSpace: "nowrap",
                        }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align='center' sx={{ py: 8 }}>
                      <Typography variant='h6' sx={{ color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
                        {translateText(
                          "No hay resultados para la búsqueda o la fecha seleccionada.",
                          lang
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:hover": {
                            backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW,
                          },
                          borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_SECONDARY}`,
                        }}>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: TEXT_COLORS.PRIMARY,
                          }}>
                          {item.mo}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: TEXT_COLORS.PRIMARY,
                          }}>
                          {formatFecha(item.fecha_ini?.date)}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: TEXT_COLORS.PRIMARY,
                          }}>
                          {item.referencia || ""}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: TEXT_COLORS.PRIMARY,
                          }}>
                          {parseFloat(item.cantidad).toFixed(2)}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: TEXT_COLORS.PRIMARY,
                            maxWidth: 200,
                          }}>
                          <Tooltip title={item.observaciones || "Sin observaciones"} arrow>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "help",
                              }}>
                              {item.observaciones || "Sin observaciones"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          {!isLoading && (
            <>
              <Divider />

              <TablePagination
                rowsPerPageOptions={[25, 50, 100, 500]}
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE,
                  "& .MuiTablePagination-toolbar": {
                    fontSize: "0.75rem",
                    color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </>
          )}
        </Paper>
      </>
    );
  };
  return (
    <Box sx={{ padding: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            value={searchText}
            onChange={handleSearchTextChange}
            placeholder={translateText("Buscar por MO", lang)}
            sx={{
              width: "100%",
              backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: (searchText || appliedSearchTerms.length > 0) 
                ? "8px 40px 8px 40px" 
                : "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>

          {(searchText || appliedSearchTerms.length > 0) && (
            <IconButton
              onClick={handleClearSearch}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                padding: 0.5,
                color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                  color: "#ef4444",
                  transform: "translateY(-50%) scale(1.1)",
                },
              }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        <Button
          onClick={handleSearchClick}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY || "#174045",
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY || "#1e4d52",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Buscar", lang)}
        </Button>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Button
          variant='outlined'
          size="small"
          startIcon={<PendingIcon />}
          onClick={() => setCurrentView("Pendientes")}
          sx={{
            backgroundColor:
              currentView === "Pendientes" ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY : "transparent",
            color: currentView === "Pendientes" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
            border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            transition: "all 0.2s ease",
            boxShadow:
              currentView === "Pendientes"
                ? "0 2px 8px rgba(70, 93, 100, 0.2)"
                : "none",
            "&:hover": {
              backgroundColor:
                currentView === "Pendientes"
                  ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY
                  : "rgba(70, 93, 100, 0.1)",
              color: currentView === "Pendientes" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Pendientes", lang)}
        </Button>

        <Button
          variant='outlined'
          size="small"
          startIcon={<HistoryIcon />}
          onClick={() => setCurrentView("Historico")}
          sx={{
            backgroundColor:
              currentView === "Historico" ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY : "transparent",
            color: currentView === "Historico" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
            border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            transition: "all 0.2s ease",
            boxShadow:
              currentView === "Historico"
                ? "0 2px 8px rgba(70, 93, 100, 0.2)"
                : "none",
            "&:hover": {
              backgroundColor:
                currentView === "Historico"
                  ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY
                  : "rgba(70, 93, 100, 0.1)",
              color: currentView === "Historico" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Historico", lang)}
        </Button>

        <Button
          variant='outlined'
          size="small"
          startIcon={<BuildIcon />}
          onClick={() => setCurrentView("Internos")}
          sx={{
            backgroundColor:
              currentView === "Internos" ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY : "transparent",
            color: currentView === "Internos" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
            border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            transition: "all 0.2s ease",
            boxShadow:
              currentView === "Internos"
                ? "0 2px 8px rgba(70, 93, 100, 0.2)"
                : "none",
            "&:hover": {
              backgroundColor:
                currentView === "Internos"
                  ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY
                  : "rgba(70, 93, 100, 0.1)",
              color: currentView === "Internos" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Rep. en taller", lang)}
        </Button>
      </Box>

      {currentView === "Internos" ? (
        renderInternoTable(repInternas)
      ) : currentView === "Pendientes" ? (
        !isLoading && filteredRepairData.length === 0 ? (
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
              textAlign: "center",
              py: 8,
            }}>
            <Typography variant='h6' sx={{ color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
              {translateText("No hay resultados para la búsqueda.", lang)}
            </Typography>
          </Paper>
        ) : (
          renderPendientesTable()
        )
      ) : (
        <>
          {!isLoading && filteredSavedReparaciones.length === 0 ? (
            <Paper
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                textAlign: "center",
                py: 8,
              }}>
              <Typography variant='h6' sx={{ color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
                {translateText("No hay resultados para la búsqueda.", lang)}
              </Typography>
            </Paper>
          ) : (
            <>{renderHistoricoTable()}</>
          )}
        </>
      )}
    </Box>
  );
}
